<template>
  <div class="page-default">
    <div class="p-4">
      <h3 class="text-light font-weight-bold mb-4">Video Report</h3>
      <div class="d-flex justify-content-between align-items-center mb-4">
        <div class="d-flex">
          <div class="dropdown">
            <button
              :disabled="isLoading"
              class="btn btn-sm btn-primary dropdown-toggle"
              type="button"
              data-toggle="dropdown"
              data-placement="top"
              aria-haspopup="true"
              aria-expanded="false"
            >
              {{ this.videoType === "dashcamvideos" ? "Dashcam" : "Drone" }}
            </button>
            <ul class="dropdown-menu">
              <li>
                <button
                  class="dropdown-item text-light"
                  :class="{
                    'bg-primary': this.videoType === 'dashcamvideos',
                  }"
                  @click="videoType = 'dashcamvideos'"
                >
                  Dashcam
                </button>
              </li>
              <li>
                <button
                  class="dropdown-item text-light"
                  :class="{
                    'bg-primary': this.videoType === 'drone-videos',
                  }"
                  @click="videoType = 'drone-videos'"
                >
                  Drone
                </button>
              </li>
            </ul>
          </div>
          <div class="dropdown ml-2">
            <button
              :disabled="isLoading"
              id="dropdownMenuButton"
              type="button"
              class="btn btn-sm"
              :class="!dateFilter ? 'btn-dark' : 'btn-primary'"
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
              data-placement="top"
              title="Filter by Date"
              @mouseover="helper.showTooltip"
              @mouseleave="helper.hideTooltip"
            >
              <font-awesome-icon :icon="['far', 'calendar-alt']" />
              <font-awesome-icon
                :icon="['fas', 'check-circle']"
                class="text-success bg-white rounded-circle"
                style="
                  font-size: 0.85em;
                  position: absolute;
                  top: 0.25em;
                  right: 0.25em;
                "
                v-if="dateFilter"
              />
              Date Filter
            </button>
            <div
              id="dropdown-menu"
              class="dropdown-menu px-2 bg-secondary"
              @click.stop
            >
              <div
                class="dropdown-item-text d-flex justify-content-between align-items-center px-0 pb-2"
              >
                <p class="badge badge-primary mb-0 font-weight-normal p-2">
                  Filter by date
                </p>
                <button
                  class="btn btn-sm btn-link text-light p-0"
                  @click="dateFilter = null"
                >
                  Clear
                </button>
              </div>
              <v-date-picker
                is-dark
                class="inline-calendar"
                :value="dateFilter"
                :model-config="{
                  type: 'string',
                  mask: 'YYYY-MM-DD',
                }"
                mode="date"
                trim-weeks
                is-range
                @input="handleClickDate"
              />
            </div>
          </div>
          <button
            type="button"
            :disabled="isLoading"
            class="btn btn-sm btn-outline-primary ml-2"
            @click="HandleModalExport()"
          >
            <font-awesome-icon
              :icon="['fas', 'file-excel']"
              class="mr-1"
            />Generate Report
          </button>
        </div>
        <div :class="{ 'd-none': isLoading || pagination.currentPage === 0 }">
          <nav aria-label="Page navigation example">
            <ul class="pagination pagination-sm">
              <li class="page-item">
                <button
                  :disabled="pagination.currentPage === 1"
                  class="page-link btn btn-dark btn-sm"
                  @click="onPrevious()"
                >
                  Previous
                </button>
              </li>
              <li class="page-item">
                <p class="page-link bg-dark text-light border-dark mb-0">
                  {{ pagination.currentPage }}
                </p>
              </li>

              <li class="page-item">
                <button
                  :disabled="pagination.currentPage === pagination.totalPage"
                  class="page-link btn btn-dark btn-sm"
                  @click="onNext()"
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      <!-- Table -->
      <p class="text-light small text-muted">
        Showing page {{ pagination.currentPage }} to
        {{ pagination.totalPage }} of entries {{ pagination.totalEntries }}
      </p>

      <div
        class="d-flex justify-content-center align-items-center w-100"
        style="min-height: 200px"
      >
        <div
          v-if="isLoading"
          class="spinner-border m-5 text-light"
          role="status"
        >
          <!-- <span class="visually-hidden">Loading...</span> -->
        </div>
        <div class="table-responsive-sm w-100" v-if="!isLoading">
          <table class="table table-dark table-sm">
            <thead>
              <tr class="small">
                <th>No.</th>
                <th>ID</th>
                <th>URL</th>
                <th>Upload Date</th>
                <th>Video Date</th>
                <th v-if="videoType === 'dashcamvideos'">Process Url</th>
                <th v-else>Process Date</th>
                <th>District</th>
                <th>Road</th>
                <th>Starting Lat Lng</th>
                <th>Status</th>
                <th>Size (MB)</th>
              </tr>
            </thead>
            <tbody>
              <tr
                class="small"
                v-for="(video, index) in dataSource"
                :key="video._id"
              >
                <td>{{ index + 1 }}.</td>
                <td>{{ video._id }}</td>
                <td style="width: 200px" class="text-break font-italic">
                  {{ !video.video ? "" : video.video.url }}
                </td>
                <td>{{ changeDatetimeFormat(video.createdAt) }}</td>
                <td>{{ changeDatetimeFormat(video.datetime) }}</td>
                <td v-if="videoType === 'dashcamvideos'">
                  <div style="width: 200px" class="text-break font-italic">
                    {{ video.processedVideo ? video.processedVideo : "-" }}
                  </div>
                </td>
                <td v-else>
                  {{
                    video.videoProcessed && video.videoProcessed.createdAt
                      ? changeDatetimeFormat(video.videoProcessed.createdAt)
                      : "-"
                  }}
                </td>
                <td>
                  {{
                    video.district && video.district.name
                      ? video.district.name
                      : "-"
                  }}
                </td>
                <td>
                  <div style="width: 200px" class="text-break">
                    {{ video.road && video.road.name ? video.road.name : "-" }}
                  </div>
                </td>
                <td>
                  {{
                    `${
                      video.district && video.district.lat
                        ? video.district.lat
                        : 0
                    }, ${
                      video.district && video.district.lng
                        ? video.district.lng
                        : 0
                    }`
                  }}
                </td>
                <td>
                  <div>
                    <span class="badge bg-info" v-if="video.isReviewed"
                      >Reviewed</span
                    >
                    <span class="badge bg-warning" v-else>Pending Review</span>
                  </div>
                </td>
                <td>
                  {{ video.videoSize ? video.videoSize : 0 }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div
        class="justify-content-between align-items-center"
        :class="{
          'd-none': isLoading || pagination.currentPage === 0,
          'd-flex': !isLoading && pagination.currentPage > 0,
        }"
      >
        <p class="text-light small text-muted">
          Showing page {{ pagination.currentPage }} to
          {{ pagination.totalPage }} of entries {{ pagination.totalEntries }}
        </p>
        <nav aria-label="Page navigation example">
          <ul class="pagination pagination-sm justify-content-end">
            <li class="page-item">
              <button
                :disabled="pagination.currentPage === 1"
                class="page-link btn btn-dark btn-sm"
                @click="onPrevious()"
              >
                Previous
              </button>
            </li>
            <li class="page-item">
              <p class="page-link bg-dark text-light border-dark mb-0">
                {{ pagination.currentPage }}
              </p>
            </li>

            <li class="page-item">
              <button
                :disabled="pagination.currentPage === pagination.totalPage"
                class="page-link btn btn-dark btn-sm"
                @click="onNext()"
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      </div>
      <modal-generate-report
        :show="generateReport.show"
        :totalEntries="pagination.totalEntries"
        :totalPage="pagination.totalPage"
        :videoType="videoType"
        :dateFilter="dateFilter"
        @close="closeModal"
      />
    </div>
  </div>
</template>

<script>
import API from "@/api";
import Helper from "@/helper";
import * as XLSX from "xlsx";
import moment from "moment";
import Swal from "sweetalert2";
import ModalGenerateReport from "@/components/ModalGenerateReport.vue";

export default {
  data() {
    return {
      videoType: "dashcamvideos",
      dataSource: [],
      isLoading: true,
      selectedItem: null,
      dateFilter: {
        start: moment().month(5).startOf("month").toISOString(),
        end: moment().month(5).endOf("month").toISOString(),
      },
      helper: Helper,
      generateReport: {
        show: false,
      },
      pagination: {
        limit: 100,
        currentPage: 0,
        totalPage: 0,
        totalEntries: 0,
      },
    };
  },
  watch: {
    videoType: {
      async handler() {
        await this.fetchingVideosTotal();
        await this.fetchingData();
      },
    },
  },
  components: {
    ModalGenerateReport,
  },
  methods: {
    async handleClickDate(date) {
      let start = moment(date.start).month();
      let end = moment(date.end).month();

      this.dateFilter = {
        start: moment().month(start).startOf("month").toISOString(),
        end: moment().month(end).endOf("month").toISOString(),
      };

      await this.fetchingVideosTotal();
      await this.fetchingData();
    },
    onNext() {
      const page = this.pagination.currentPage;
      this.pagination.currentPage = page + 1;
      this.fetchingData();
    },
    onPrevious() {
      const page = this.pagination.currentPage;
      this.pagination.currentPage = page - 1;
      this.fetchingData();
    },
    async fetchingVideosTotal() {
      const start = this.dateFilter.start;
      const end = this.dateFilter.end;

      await API.get(
        `${this.videoType}/count?_limit=100&datetime_gte=${start}&datetime_lte=${end}&_start=0`
      )
        .then(async (response) => {
          const totalPage = Math.ceil(response.data / 100);

          this.pagination = {
            currentPage: totalPage > 0 ? 1 : 0,
            totalPage,
            totalEntries: response.data,
            limit: 100,
          };

          this.fetchingData();
        })
        .catch((err) => {
          this.isLoading = false;
          Swal.fire({
            icon: "error",
            title: "Oops...",
            text: `Something went wrong! ${err}`,
          });
        });
    },
    async fetchingData() {
      this.dataSource = [];
      this.isLoading = true;

      let params = ``;
      let url = new URLSearchParams(params);

      url.append("_limit", this.pagination.limit);
      url.append("_sort", "datetime:DESC");

      if (this.dateFilter && this.dateFilter.start && this.dateFilter.end) {
        url.append("datetime_gte", this.dateFilter.start);
        url.append("datetime_lte", this.dateFilter.end);
      }

      const currentIdx =
        this.pagination.currentPage === 1 ? 0 : this.pagination.currentPage - 1;
      const pageNumber = currentIdx > 0 ? currentIdx * 100 : 0;

      if (this.pagination) {
        url.append(
          "_start",
          this.pagination.currentPage === 1 ? 0 : pageNumber
        );
      }

      await API.get(`${this.videoType}?${url.toString()}`)
        .then((response) => {
          this.dataSource = response.data.map((obj) => {
            let size = 0;

            const vSize = obj.video && obj.video.size ? obj.video.size : 0;
            const pSize =
              obj.videoProcessed && obj.videoProcessed.size
                ? obj.videoProcessed.size
                : 0;

            size = vSize + pSize;
            return {
              ...obj,
              videoSize: this.helper.autoFormatSize(size),
            };
          });
          this.isLoading = false;
        })
        .catch((err) => {
          this.isLoading = false;
          Swal.fire({
            icon: "error",
            title: "Oops...",
            text: `Something went wrong! ${err}`,
          });
        });
    },
    changeDatetimeFormat(dateTime) {
      return moment(dateTime).format("YYYY-MM-DD HH:mm:ss");
    },
    async HandleModalExport(data) {
      if (!this.pagination.totalEntries) return;
      this.generateReport = {
        show: true,
      };
    },
    formatingData(data) {
      return [
        {
          id: data.id,
          url: data.url,
          uploadDate: data.uploadDate,
          videoDate: data.videoDate,
          district: data.district,
          road: data.road,
          latLng: data.latLng,
          status: data.status,
        },
      ];
    },
    closeModal() {
      this.generateReport.show = false;
    },
  },
  async mounted() {
    await this.fetchingVideosTotal();
  },
};
</script>